import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from './WebSocketContext';

const folderRestrictionsCache = new Map();
const folderRestrictionsInFlight = new Map();

/**
 * Hook to fetch and manage folder restriction settings for a case
 * Returns restriction state that can be passed to useFolderPicker
 */
export function useFolderRestrictions(caseid) {
  const { sendMessage, subscribeToMessages, isUserLoggedIn, isValidatingSession, userInfo } = useWebSocket() || {};
  const cacheKey = `${userInfo?.organization_id || ''}:${userInfo?.id || ''}:${caseid || ''}`;
  const cached = caseid ? folderRestrictionsCache.get(cacheKey) : null;
  const [restrictToUserFolder, setRestrictToUserFolder] = useState(cached?.restrictToUserFolder || false);
  const [allowedRoots, setAllowedRoots] = useState(cached?.allowedRoots || []);
  const [displayRoot, setDisplayRoot] = useState(cached?.displayRoot || '');
  const [pathRoots, setPathRoots] = useState(cached?.pathRoots || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const retry = useCallback(() => setRequestVersion(version => version + 1), []);

  useEffect(() => {
    const current = caseid ? folderRestrictionsCache.get(cacheKey) : null;
    setRestrictToUserFolder(current?.restrictToUserFolder || false);
    setAllowedRoots(current?.allowedRoots || []);
    setDisplayRoot(current?.displayRoot || '');
    setPathRoots(current?.pathRoots || []);
    setLoading(Boolean(caseid && !current));
    setError(false);
  }, [caseid, cacheKey]);

  useEffect(() => {
    if (!caseid) {
      setLoading(false);
      return;
    }

    if (isValidatingSession) {
      return;
    }

    if (!isUserLoggedIn) {
      folderRestrictionsCache.clear();
      folderRestrictionsInFlight.clear();
      setLoading(false);
      return;
    }

    // Deduplicate active requests, but release them when the last reader leaves.
    const existingRequest = folderRestrictionsInFlight.get(cacheKey);
    const request = existingRequest || { readers: 0 };
    request.readers += 1;
    folderRestrictionsInFlight.set(cacheKey, request);
    let timeout;
    const fail = () => {
      clearTimeout(timeout);
      if (folderRestrictionsInFlight.get(cacheKey) === request) folderRestrictionsInFlight.delete(cacheKey);
      folderRestrictionsCache.delete(cacheKey);
      setRestrictToUserFolder(true);
      setAllowedRoots([]); setDisplayRoot(''); setPathRoots([]);
      setLoading(false);
      setError(true);
    };
    const unsubscribe = subscribeToMessages(['getFoldersSettings'], (parsedMessage) => {
      if (parsedMessage.command === 'getFoldersSettings') {
        const data = parsedMessage.data || {};
        if (String(data.caseid) !== String(caseid)) return;
        clearTimeout(timeout);
        if (data.status === 'error' || data.error) {
          fail();
          return;
        }
        setRestrictToUserFolder(data.restrictToUserFolder || false);
        setAllowedRoots(data.allowedRoots || []);
        setDisplayRoot(data.displayRoot || '');
        setPathRoots(data.pathRoots || []);
        setLoading(false);
        setError(false);
        folderRestrictionsCache.set(cacheKey, {
          restrictToUserFolder: data.restrictToUserFolder || false,
          allowedRoots: data.allowedRoots || [],
          displayRoot: data.displayRoot || '',
          pathRoots: data.pathRoots || []
        });
        if (folderRestrictionsInFlight.get(cacheKey) === request) folderRestrictionsInFlight.delete(cacheKey);
      }
    });

    // Request folder settings
    setLoading(true);
    setError(false);
    timeout = setTimeout(fail, 15000);
    if (!existingRequest) {
      try {
        if (sendMessage({ command: 'getFoldersSettings', caseid }) === false) fail();
      } catch {
        fail();
      }
    }

    return () => {
      clearTimeout(timeout);
      unsubscribe();
      request.readers -= 1;
      if (!request.readers && folderRestrictionsInFlight.get(cacheKey) === request) {
        folderRestrictionsInFlight.delete(cacheKey);
      }
    };
  }, [caseid, cacheKey, isUserLoggedIn, isValidatingSession, sendMessage, subscribeToMessages, requestVersion]);

  return {
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    pathRoots,
    loading,
    error,
    retry
  };
}
